/**
 * REASON-NL-1 (2026-10-13): the backend writes `buy_reason` in English. These are the sentences a
 * buyer meets on a "not a pick" card (about 95% of them, measured on the served table), in Dutch
 * with their numbers kept. Unknown text is shown as written: a missing translation must not hide
 * a reason.
 */
const eur = digits => `€${String(digits).replace(/,/g, '.')}`

const NL = [
  [/^listed far longer than its model's norm/, () =>
    'Staat veel langer online dan gebruikelijk voor dit model: waarschijnlijk al verkocht, of er is iets mis mee'],
  [/^asking ([\d.]+)% below its own-country model\/fuel\/year median/, m =>
    `Vraagprijs ${m[1].replace('.', ',')}% onder het gemiddelde in het eigen land voor dit model en bouwjaar: controleer schade, prijsvorm (lease, export) en of het geen oplichting is`],
  [/^asking price is over 25% below its own-country market/, () =>
    'Vraagprijs meer dan 25% onder de markt in het eigen land: controleer schade, prijsvorm en of het geen oplichting is'],
  [/^margin implausibly high for age/, () =>
    'Marge onwaarschijnlijk hoog voor de leeftijd: waarschijnlijk een lokprijs of een leasebedrag'],
  [/^fewer than (\d+) Dutch comparables for a car of this age/, m =>
    `Minder dan ${m[1]} vergelijkbare Nederlandse auto's voor een auto van deze leeftijd: te weinig vraagprijzen om van een markt te spreken`],
  [/^listed for over (\d+) days at this price/, m =>
    `Staat al meer dan ${m[1]} dagen te koop voor deze prijs: de markt heeft hem laten liggen`],
  [/^fewer than (\d+) Dutch comparables on a car above EUR ([\d,]+)/, m =>
    `Minder dan ${m[1]} vergelijkbare Nederlandse auto's bij een auto boven ${eur(m[2])}: te weinig bewijs voor zoveel geld`],
  [/^asking far below value band/, () =>
    'Vraagprijs ver onder onze waardeband: waarschijnlijk schade, een ongeval of een datafout'],
  [/^too few Dutch comparables to value an older car \(need (\d+)\+\)/, m =>
    `Te weinig vergelijkbare Nederlandse auto's om een oudere auto te waarderen (minstens ${m[1]} nodig)`],
  [/^published low-end outcome loses more than EUR ([\d,]+)/, m =>
    `Aan de onderkant van onze bandbreedte verlies je meer dan ${eur(m[1])}: te veel risico om aan te raden`],
  [/^near-new car/, () =>
    'Vrijwel nieuwe auto: bij import betaal je 21% btw, dus geen importvoordeel, en de Nederlandse waarde is begrensd door de nieuwprijs'],
  [/^valued (only|partly) from other trims; Dutch asks for its own trim \((.+?)\) sit (\d+)% lower \(about EUR ([\d,]+), (\d+) cars?\)/, m =>
    `Gewaardeerd op ${m[1] === 'only' ? 'alleen' : 'deels'} andere uitvoeringen; Nederlandse vraagprijzen voor de uitvoering ${m[2]} liggen ${m[3]}% lager (ongeveer ${eur(m[4])}, ${m[5]} auto's)`],
  [/^condition risk too large relative to the price/, () =>
    'Conditierisico te groot ten opzichte van de prijs: reparaties kunnen meer dan een derde van de aankoopprijs kosten'],
  [/^margin (\d+)% is below the (\d+)% our valuation uncertainty for this car requires/, m =>
    `Marge ${m[1]}% is lager dan de ${m[2]}% die onze onzekerheid voor deze auto vraagt`],
  [/^upside disappears once the asking-to-sale spread/, () =>
    'De winst verdwijnt na de onderhandelruimte bij verkoop en de reserve voor onzichtbare gebreken'],
  [/^the seller has raised the price since our snapshot \(\+(\d+)%\)/, m =>
    `De verkoper heeft de prijs met ${m[1]}% verhoogd sinds onze momentopname: de marge is weg`],
  [/^the listing itself states a defect/, () => 'De advertentie zelf meldt een gebrek'],
  [/^source verification: cash price unconfirmed/, () =>
    'Dealerpagina gelezen, maar een contante prijs is niet bevestigd: vraag de verkoper naar de prijs zonder financiering'],
]

/** Dutch for a known backend reason, else the reason unchanged. */
export function reasonText(reason, lang) {
  if (lang !== 'nl' || typeof reason !== 'string') return reason
  for (const [pattern, dutch] of NL) {
    const match = reason.match(pattern)
    if (match) return dutch(match)
  }
  return reason
}

// pricer_core.TIER_MEANING, the core of every match story, longest first so none is cut short.
const TIER_NL = [
  ['same model, fuel, gearbox and body; power +/-20%, wider year and km windows', 'zelfde model, brandstof, transmissie en carrosserie; vermogen ±20%, ruimere bouwjaar- en km-grenzen'],
  ['kernel over the whole same-fuel pool, weighted by year and km distance', 'gewogen over alle Nederlandse auto\'s met dezelfde brandstof, naar verschil in bouwjaar en km'],
  ['same model, fuel and gearbox; body ignored, wide year and km windows', 'zelfde model, brandstof en transmissie; carrosserie genegeerd, ruime bouwjaar- en km-grenzen'],
  ['same model and fuel only; gearbox, body and power ignored', 'alleen zelfde model en brandstof; transmissie, carrosserie en vermogen genegeerd'],
  ['same model and fuel, no NL year match; nearest years', 'zelfde model en brandstof, geen Nederlandse auto\'s van dit bouwjaar; dichtstbijzijnde bouwjaren'],
  ['same model, fuel, gearbox, body, power and battery', 'zelfde model, brandstof, transmissie, carrosserie, vermogen en accu'],
  ['same model, fuel, gearbox, body and power (+/-10%)', 'zelfde model, brandstof, transmissie, carrosserie en vermogen (±10%)'],
  ['same model, fuel and trim; gearbox ignored', 'zelfde model, brandstof en uitvoering; transmissie genegeerd'],
  ['same model and fuel; very wide year window', 'zelfde model en brandstof; zeer ruime bouwjaargrens'],
  ['same model and fuel; nearest years', 'zelfde model en brandstof; dichtstbijzijnde bouwjaren'],
]
// The other clauses pricer_core joins onto it ("; "), specific before general.
const STORY_NL = [
  [/^(\d+) Dutch (.+?) comparable\(s\): /, '$1 Nederlandse $2 ter vergelijking: '],
  [/\btier(\w+): (\d+) comps centred on (\d{4})/g, "niveau $1: $2 auto's rond bouwjaar $3"],
  [/\byears (\d{4})-(\d{4})/g, 'bouwjaren $1-$2'],
  [/\b(\d+)-(\d+)k km/g, '$1-$2 duizend km'],
  [/comps centred on another era \((.+?)\), prices age-adjusted/g, 'vergelijkingsauto\'s uit een andere periode ($1), prijzen omgerekend'],
  [/prices age-adjusted to (\d{4})/g, 'prijzen omgerekend naar bouwjaar $1'],
  [/prices age-adjusted/g, 'prijzen omgerekend naar dit bouwjaar'],
  [/same trim \((.+?)\) from a wider year\/km window; the (.+?) comps were all other trims/,
    'zelfde uitvoering ($1) uit een ruimer bouwjaar- en km-venster; de $2-vergelijkingsauto\'s waren allemaal andere uitvoeringen'],
  [/all comparables are other trims than (.+)$/, 'alle vergelijkingsauto\'s zijn andere uitvoeringen dan $1'],
  [/^no defensible valuation: (\d+) relaxed Dutch (.+?) comparables are centred on (\d{4}), more than (\d+) years from (\d{4}); condition and facelift risk are not observable/,
    'geen verdedigbare waardering: $1 Nederlandse $2 ter vergelijking liggen rond bouwjaar $3, meer dan $4 jaar van $5; staat en facelift-risico zijn niet zichtbaar'],
  [/^no comparables; nearest sets were centred on another era: /, 'geen vergelijkingsauto\'s; de dichtstbijzijnde sets lagen in een andere periode: '],
]

/** The backend's one-sentence basis of a valuation (`match_story`), in Dutch clause by clause.
 * A clause it does not know stays as written. */
export function storyText(story, lang) {
  if (lang !== 'nl' || typeof story !== 'string') return story
  let text = story
  for (const [en, dutch] of TIER_NL) text = text.split(en).join(dutch)
  for (const [pattern, dutch] of STORY_NL) text = text.replace(pattern, dutch)
  return text
}

export const MATCH_STORY_TIER_CLAUSES = TIER_NL.map(([en]) => en)
